import React, { useEffect, useState, useContext } from 'react'

//Node Modules
import { useHistory, useLocation } from 'react-router-dom'
import axios from 'axios'
import { useTranslation, Trans } from 'react-i18next'

//Components
import CDC from 'components/CDC'
import Loading from 'components/Loading'
import Document from 'components/Document'
import NotFound from 'components/NotFound'
import SecureModal from 'components/SecureModal'
import * as service from './service'

//Styles
import styles from './CDC.module.scss'

//Context
import { CDCContext, DocsetContext, SecureContext, ToggleSecureContext, ToggleUnavailableContext, CurrentContentContext, FeatureFlagsContext, RequestorContext } from 'context/content-context'
import { AvailableLanguagesContext } from 'context/language-context'
import { MessagesDispatch } from 'context/messages-context'
import {CasFormatDispatch, CasFormatState, CasLanguageState } from 'context/cas-format-context'
import {LoadingContext} from 'context/loading-context'

//Api
import { content as retrieveContent } from 'api/docset'
import * as api from 'api/secure'
import {defaultContext as retrieveDefaultContext} from 'api/cdc'
import { defaultContext as retrieveDocsetDefault} from 'api/docset'
import { trackDownload } from 'api/tracking'

//Types
import { CDC as ICDC, DefaultContext } from 'types/cdc'
import Docset, {Content} from 'types/docset'
import {Format} from 'types/format'

//Util
import {cdcPdfLink} from 'util/links'
import { HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_PAGE_NOT_FOUND, HELP_DOCSET_UNAUTHORIZED, DOCSET_PAGE_TITLEPAGE, VERSION_GROUP_VIYA4, BANNER_UNAVAILABLE_KEY, BANNER_UNSUPPORTED_KEY } from 'util/constants'

interface Props {
  lang: string
  alias: string
  cfr: string
  docsetAlias: string
  page: string
  help?: boolean,
  homeOnFail?: boolean
}

const CDCContainer = ({ lang, alias, cfr,docsetAlias, page, help, homeOnFail }: Props) => {
  const [cdc, setCDC] = useState<ICDC>()
  const [cdcNotFound, setCDCNotFound] = useState(false)
  const [docset, setDocset] = useState<Docset>()
  const [docsetNotFound, setDocsetNotFound] = useState(false)
  const [content, setContent] = useState<Content>()
  const [showSecureModal, setShowSecureModal] = useState(false)
  const [pdfUrl, setPdfUrl] = useState<string | undefined>(undefined)
  const [casPdfUrl, setCasPdfUrl] =useState<string | undefined>(undefined)
  const [defaultContext, setDefaultContext] = useState<DefaultContext|undefined>(undefined)
  const [docsetDefault, setDocsetDefault] = useState<DefaultContext|undefined>(undefined)
  const messagesDispatch = useContext(MessagesDispatch)
  const casFormatDispatch = useContext(CasFormatDispatch)
  const casFormatState = useContext(CasFormatState)
  const casLanguage = useContext(CasLanguageState)
  const secureContext = useContext(SecureContext)
  const toggleSecureContext = useContext(ToggleSecureContext)
  const toggleUnavailableContext = useContext(ToggleUnavailableContext)
  const history = useHistory()
  const loadingContext = useContext(LoadingContext)
  const currentContentContext = useContext(CurrentContentContext)
  const requestorId = useContext(RequestorContext)
  const [initialLoad, setInitialLoad] = useState(true)
  const [homeFail, setHomeFail] = useState(homeOnFail)
  const location = useLocation()
  const { t } = useTranslation()

  const versions = cdc?.versions
  const currentVersion = versions?.find(v => v.version === cfr)
  const isViya4 = currentVersion?.versionGroup === VERSION_GROUP_VIYA4
  
  function loadPageNotFound() {
    if (homeOnFail) {
      setHomeFail(true)
      messagesDispatch({ type: 'add', message: { key: BANNER_UNAVAILABLE_KEY, content: t('helpcenter-gui-icu.message.page_unavailable.msg'), style: 'warning', closeable: true}})
      
      if (cdc?.info.cdcDocsets?.docsets.find(d => d ? d.id === docsetAlias : false)) {
        history.replace(`/doc/${lang}/${alias}/${cfr}/${docsetAlias}/${DOCSET_PAGE_TITLEPAGE}`)
      } else {
        service.home(alias, cfr)
          .then(home => history.replace(`/doc/${lang}/${alias}/${cfr}/${home ? home.docset : docsetAlias}/${home.file}`))
          .catch(() => {
            retrieveContent(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_PAGE_NOT_FOUND)
              .then(result => {
                currentContentContext.setCurrentContentContext(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_PAGE_NOT_FOUND)
                setContent(result)
              })
          })
      }
    } else {
      retrieveContent(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_PAGE_NOT_FOUND)
        .then(result => {
          currentContentContext.setCurrentContentContext(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_PAGE_NOT_FOUND)
          setContent(result)
        })
    }
  }

  useEffect (()=> {
    if (toggleUnavailableContext.unavailableToggled.trim() !== '') {
      retrieveContent(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, 'unavailable.htm')
      .then(result => {
        currentContentContext.setCurrentContentContext(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, 'unavailable.htm')
        setContent(result)
      })
    }
  }, [toggleUnavailableContext.unavailableToggled])/* eslint-disable-line */

  useEffect(()=> {
    //don't resend all rest calls if this is first page load
    if ( initialLoad) return
    loadingContext.setLoadingContext(true)
    service.retrieveCDC(alias, cfr, requestorId).then(setCDC)
  }, [toggleSecureContext.secureToggled])/* eslint-disable-line */

  useEffect(() => {
    setInitialLoad(false)
    setCDC(undefined)
    service.retrieveCDC(alias, cfr, requestorId).then( cdc => {
      setCDC(cdc)
    }).catch(() => setCDCNotFound(true))
    retrieveDefaultContext(alias,cfr).then(setDefaultContext)
  }, [lang, alias, cfr])/* eslint-disable-line */

  useEffect(() => {
    setDocset(undefined)
    setDocsetNotFound(false)

    if (!cdc || cdc.info.id !== alias) {
      return
    }

    const currDocset = cdc.info.cdcDocsets?.docsets.find(d => d ? d.id === docsetAlias : false)
    
    if (!currDocset) {
      setDocset(undefined)
      loadPageNotFound()
      return
    }
    
    retrieveDocsetDefault(currDocset.id, currDocset.version).then(setDocsetDefault,() => setDocsetDefault(undefined))  
    service.retrieveDocset(lang, currDocset.id, currDocset.version, requestorId)
      .then(docset => {
        setDocset({
          ...docset,
          info: currDocset
        })
      })
      .catch(() => {
        setDocset(undefined)
        setDocsetNotFound(true)
      })
  }, [lang, cdc, docsetAlias])/* eslint-disable-line */

  useEffect(() => {
    toggleUnavailableContext.toggleUnavailable('')

    //clear cas formats
    casFormatDispatch({ type: 'clear'})
    setCasPdfUrl(undefined)
    setPdfUrl(undefined)
    
    loadingContext.setLoadingContext(true)
    secureContext.setSecureContext(false)
    setShowSecureModal(false)
    if (docsetNotFound) {
      // Load Page Not Found
      loadPageNotFound()
    }

    if (cdc?.info.id !== alias || !docset) {
      return
    }

    let newDocset = cdc?.info.cdcDocsets?.docsets.find(d=>{ return d && d.id === docsetAlias})

    // Check if page is specified as help page
    const [pageAlias, pageCFR] = help ? [HELP_DOCSET_ALIAS, HELP_DOCSET_CFR] : [docsetAlias || '', newDocset ? newDocset.version : docset?.info.version || '']
    
    currentContentContext.setCurrentContentContext(pageAlias,pageCFR,page)
    //check to see if cdc is supported
    if (cdc.info.versionGroup === VERSION_GROUP_VIYA4) {
      let currVersionCdc = cdc.versions.find( c =>{
        return c.version === cfr
      })
      let latestCdc = cdc.versions[0]
      if (currVersionCdc && currVersionCdc.version !== latestCdc.version && currVersionCdc.supportedInd === false) {
        let latestCdcUrl = `./doc/${lang}/${latestCdc.name}/${latestCdc.version}/${docsetAlias}/${page}?homeOnFail`
        let transText = <Trans i18nKey='helpcenter-gui-icu.message.page_unsupported.msg'> <a className={styles.unsupportedLink} href={latestCdcUrl}></a></Trans>/* eslint-disable-line */
        messagesDispatch({ type: 'remove', message: { key: BANNER_UNSUPPORTED_KEY}})
        messagesDispatch({ type: 'prepend', message: { key: BANNER_UNSUPPORTED_KEY, content: transText, style: 'error', closeable: false}})
      } else {
        messagesDispatch({ type: 'remove', message: { key: BANNER_UNSUPPORTED_KEY}})
      }
    }
    retrieveContent(pageAlias, pageCFR, page)
    .then(result => {
      setContent(result)
      loadingContext.setLoadingContext(false)
    })
    .catch((err) => {
      //if unauthorized
      if ( err.response && err.response.status === 401) {
        setContent({alias: pageAlias, cfr: pageCFR, page: page, content: ''})
        loadingContext.setLoadingContext(false)
        secureContext.setSecureContext(true)
        showModal()
        return
      } else {
        loadPageNotFound()
      }
    })
  }, [lang, alias, cfr, docset, page, help, docsetNotFound])/* eslint-disable-line */

  useEffect(()=>{
    if (homeFail) {
      setHomeFail(false)
    } else {
      messagesDispatch({type:'remove', message: { key: BANNER_UNAVAILABLE_KEY}})
    }
  },[location])/* eslint-disable-line */

  useEffect(()=>{
    if (!docset || initialLoad) return
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    let dest = page.substring(0, page.lastIndexOf('.'))
    let casPdfLink = cdcPdfLink(lang, alias, cfr, docsetAlias, casLanguage, casFormatState)
    if (casPdfLink) {
      casPdfLink.then(url => setCasPdfUrl(`${url}#nameddest=${dest}`))
    } else {
      setCasPdfUrl(undefined)
    } 
    service.retrieveUrl(source, alias, cfr, docsetAlias, Format.Pdf, lang).then(url => {setPdfUrl(`${url}#nameddest=${dest}`)}).catch(()=> setPdfUrl(undefined) )
    return () => source.cancel()
  },[lang, casFormatState, currentContentContext.currentContent, casLanguage,])/* eslint-disable-line */

  const noAccessKey = () => {
    setShowSecureModal(false)
    setPdfUrl(undefined)
    setCasPdfUrl(undefined)
    retrieveContent(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_UNAUTHORIZED).then(result => {
      currentContentContext.setCurrentContentContext(HELP_DOCSET_ALIAS, HELP_DOCSET_CFR, HELP_DOCSET_UNAUTHORIZED)
      setContent(result)
    })
  }

  const showModal = () => {
    setShowSecureModal(true)
  } 

  const submitKey = (accessKey: string): Promise<void> => {
    return api.cdcSecureKey(alias, cfr, accessKey)
  }

  const info = docset && docset.info
  const languages = (docset && docset.languages) || []
  const contentLanguage = info?.language
  const defaultFlags = (docset && docset.featureFlags) || []

  const trackPDFDownload = () => {
    trackDownload('pdf', 'cdc', lang, cdc!.info.id, cdc!.info.version, docsetAlias, page)
  }

  return (
    <>
        {
          cdc ?
            <CDCContext.Provider value={cdc}>
              <DocsetContext.Provider value={info}>
                <FeatureFlagsContext.Provider value={defaultFlags}>
                  <AvailableLanguagesContext.Provider value={languages}>
                    <SecureModal show={showSecureModal} onHide={noAccessKey} submit={submitKey}/>
                    <CDC
                      cdc={cdc}
                      defaultContext={defaultContext}
                      docset={docset}
                      currDocset={docsetAlias}
                      defaultPage={docsetDefault?.file}
                      page={page}
                      pdfUrl={casPdfUrl ? casPdfUrl : pdfUrl}
                      onPDFDownloadClick={trackPDFDownload}
                      content={
                      <Document alias={alias} cfr={cfr} page={page} content={content} help={help} lang={contentLanguage} isViya4={isViya4}/>
                      } 
                    />
                  </AvailableLanguagesContext.Provider>
                </FeatureFlagsContext.Provider>
              </DocsetContext.Provider>
            </CDCContext.Provider> 
          :
            cdcNotFound ? <NotFound language={lang} /> : <Loading />
        }
    </>
  )
}

export default CDCContainer